import React from "react";
import { Card, CardContent } from "../../components/ui/card";
import { formatValue, displayLineItemName } from "../../lib/utils";

interface BalanceSheetTabProps {
  financialData: any;
}

const BalanceSheetTab: React.FC<BalanceSheetTabProps> = ({ financialData }) => {
  const financials = financialData || [];
  const years = financials.map((f: any) => {
    const year = f.year.split("-")[0];
    return `FY ${(parseInt(year) - 1).toString()} - ${year
      .toString()
      .slice(-2)}`;
  });

  const nature = financials.find((f: any) => f.nature)?.nature || 'Standalone';
  
  // Detect if this is LLPIN data (check if any record was transformed from LLPIN)
  const isLLPIN = financials.some((f: any) => 
    (f as any).isLLPIN || 
    (f as any).statement_of_assets_and_liabilities || 
    (f as any).statement_of_income_and_expenditure
  );

  // Asset sections with proper grouping
  const assetSections = [
    {
      title: "Non-Current Assets",
      items: [
        "tangible_assets",
        "producing_properties",
        "intangible_assets",
        "preproducing_properties",
        "tangible_assets_capital_work_in_progress",
        "intangible_assets_under_development",
        "noncurrent_investments",
        "deferred_tax_assets_net",
        "foreign_curr_monetary_item_trans_diff_asset_account",
        "long_term_loans_and_advances",
        "other_noncurrent_assets",
      ],
      isSubTotal: true,
    },
    {
      title: "Current Assets",
      items: [
        "current_investments",
        "inventories",
        "trade_receivables",
        "cash_and_bank_balances",
        "short_term_loans_and_advances",
        "other_current_assets",
      ],
      isSubTotal: true,
    },
  ];

  // Liability sections with proper grouping
  const liabilitySections = [
    {
      title: isLLPIN ? "Partner's Funds" : "Shareholders' Fund",
      items: [
        "share_capital",
        "reserves_and_surplus",
        "money_received_against_share_warrants",
        "share_application_money_pending_allotment",
        "deferred_government_grants",
        "minority_interest",
      ],
      isSubTotal: true,
    },
    {
      title: isLLPIN ? "Liabilities" : "Non-Current Liabilities",
      items: [
        "long_term_borrowings",
        "deferred_tax_liabilities_net",
        "foreign_curr_monetary_item_trans_diff_liability_account",
        "other_long_term_liabilities",
        "long_term_provisions",
      ],
      isSubTotal: true,
    },
    {
      title: isLLPIN ? "" : "Current Liabilities",
      items: [
        "short_term_borrowings",
        "trade_payables",
        "other_current_liabilities",
        "short_term_provisions",
      ],
      isSubTotal: true,
    },
  ];


  const LLPINAssetSections = [
    {
      title: "Assets",
      items: [
        "gross_fixed_assets",
        "depreciation_and_amortization",
        "investments",
        "loans_and_advances",
        "inventories",
        "trade_receivables",
        "cash_and_cash_equivalents",
        "other_assets",
        "net_fixed_assets"
      ],
      isSubTotal: false,
    },
    
  ];

  const LLPINLiabilitySections = [
    {
      title: "Partner's Funds",
      items: [
        "contribution_received",
        "reserves_and_surplus"
      ],
      isSubTotal: false,
    },
    {
      title: "Liabilities",
      items: [
        // "contribution_received",
        // "reserves_and_surplus",
        "secured_loan",
        "unsecured_loan",
        "short_term_borrowing",
        "trade_payables",
        "other_liabilities",
        // "provisions_for_taxation",
        // "provisions_for_contingencies",
        // "provisions_for_insurance",
        "other_provisions"
      ],
      isSubTotal: false,
    },
    {
      title: "Provisions",
      items: [
        "provisions_for_taxation",
        "provisions_for_contingencies",
        "provisions_for_insurance",
        // "other_provisions"
      ],
      isSubTotal: false,

    },
    
  ];



  const calculateSubTotal = (
    items: string[],
    dataKey: "assets" | "liabilities",
    yearIndex: number
  ) => {
    return items.reduce((sum, item) => {
      const value = financials[yearIndex]?.bs?.[dataKey]?.[item];
      return sum + (Number(value) || 0);
    }, 0);
  };

  const calculateTotal = (
    sections: any[],
    dataKey: "assets" | "liabilities",
    yearIndex: number
  ) => {
    return sections.reduce((total, section) => {
      const sectionTotal = section.items.reduce((sum: number, item: string) => {
        // For LLPIN assets, exclude gross_fixed_assets and depreciation_and_amortization
        if (isLLPIN && dataKey === "assets" && 
            (item === "gross_fixed_assets" || item === "depreciation_and_amortization")) {
          return sum;
        }
        
        const financial = financials[yearIndex];
        const value = isLLPIN
          ? financial?.statement_of_assets_and_liabilities?.[dataKey]?.[item]
          : financial?.bs?.[dataKey]?.[item];
        return sum + (Number(value) || 0);
      }, 0);
      return total + sectionTotal;
    }, 0);
  };

  const renderSection = (
    sections: any[],
    dataKey: "assets" | "liabilities",
    title: string
  ) => (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          {title === "Assets" ? "📈" : "📊"} {title}
        </h4>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
        <div className="relative">
          {/* Header using CSS Grid like P&L */}
          <div className="w-full overflow-x-auto">
            <div
              className="grid"
              style={{
                minWidth: `${240 + years.length * 160}px`,
                gridTemplateColumns: `minmax(240px,1fr) repeat(${years.length}, minmax(160px,1fr))`,
              }}
            >
              {/* Header Row */}
              <div className="bg-gray-50 p-4 font-semibold border-b-2 border-r-2 border-gray-300 sticky top-0 left-0 z-40">
                Line Item
              </div>
              {years.map((year: any, index: any) => (
                <div
                  key={index}
                  className="bg-gray-50 p-4 font-semibold text-center border-b-2 border-r border-gray-200 sticky top-0 z-30"
                >
                  {year}
                </div>
              ))}

              {/* Data Rows using CSS Grid like P&L */}
              {sections.map((section, sectionIndex) => (
                <React.Fragment key={sectionIndex}>
                  {/* Section Header */}
                  <div className="p-3 font-semibold text-blue-800 border-b border-r-2 border-gray-300 bg-blue-100 sticky left-0 z-20">
                    {section.title}
                  </div>
                  {years.map((_: any, yearIndex: any) => (
                    <div
                      key={yearIndex}
                      className="p-3 border-b border-r border-gray-200 bg-blue-100"
                    ></div>
                  ))}

                  {/* Section Items */}
                  {section.items.map((lineItem: string) => (
                    <React.Fragment key={lineItem}>
                      {/* Line Item Name (Frozen Column) */}
                      <div className="p-3 border-b border-r-2 border-gray-300 sticky left-0 z-10 bg-white hover:bg-gray-50">
                        <span className="text-sm text-gray-700 pl-4">
                          {displayLineItemName(lineItem, isLLPIN)}
                        </span>
                      </div>

                      {/* Values for each year */}
                      {financials.map((financial: any, colIndex: any) => (
                        <div
                          key={`${lineItem}-${colIndex}`}
                          className="p-3 text-right border-b border-r border-gray-200 hover:bg-gray-50"
                        >
                          { isLLPIN ? formatValue(financial.statement_of_assets_and_liabilities?.[dataKey]?.[lineItem]) : formatValue(financial.bs?.[dataKey]?.[lineItem])}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* Sub Total */}
                  {section.isSubTotal && (
                    <React.Fragment>
                      <div className="p-3 font-semibold text-blue-800 border-b-2 border-r-2 border-gray-300 bg-blue-50 sticky left-0 z-20">
                        Sub Total
                      </div>
                      {financials.map((_: any, yearIndex: number) => (
                        <div
                          key={yearIndex}
                          className="p-3 text-right font-semibold text-blue-700 border-b-2 border-r border-gray-200 bg-blue-50"
                        >
                          {formatValue(
                            calculateSubTotal(section.items, dataKey, yearIndex)
                          )}
                        </div>
                      ))}
                    </React.Fragment>
                  )}

                  
                </React.Fragment>
              ))}

              {/* Total Row */}
              <div className="p-4 font-bold text-green-800 border-t-4 border-r-2 border-gray-300 bg-green-50 sticky left-0 z-20">
                Total {title}
              </div>
              {financials.map((_: any, yearIndex: number) => (
                <div
                  key={yearIndex}
                  className="p-4 text-right font-bold text-green-700 border-t-4 border-r border-gray-200 bg-green-50"
                >
                  {formatValue(calculateTotal(sections, dataKey, yearIndex))}  
                </div>
              ))}
            </div>
          </div>

          {/* Expanded/Collapsed content based on state */}
          {/* {!isExpanded && (
            <div className="text-center p-4 text-gray-500 text-sm border-t">
              Click "Expand" to show more details
            </div>
          )} */}
        </div>
      </div>
    </div>
  );

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Balance Sheet</h3>
          {/* <p className="truncate break-words max-w-xs hover:overflow-visible hover:whitespace-normal hover:break-words">
            Financial Years: {years.join(', ')}
          </p> */}
          <h4>{nature.charAt(0).toUpperCase() + nature.slice(1)}</h4>
        </div>

        {/* Assets Section */}
        {renderSection(isLLPIN ? LLPINAssetSections : assetSections, "assets", "Assets")}

        {/* Liabilities Section */}
        {renderSection(
          isLLPIN ? LLPINLiabilitySections : liabilitySections,
          "liabilities",
          "Liabilities & Equity"
        )}
      </CardContent>
    </Card>
  );
};

export default BalanceSheetTab;
